import { ReactElement, useMemo } from 'react';
import { formatISO, startOfYear, endOfYear } from 'date-fns';
import { DateRangePeriodPicker, VisibilityProps, Visibility } from '../common';
import { columns } from './columns';
import { EnterpriseField, ReportingGroupField } from 'components';
import type { Row } from './types';
import { CompanyField } from 'modules/core/company/components';
import { ReportTable } from 'components/ReportTable';
import { TableColumns } from '@ff-it/ui';
import { OutletOrServiceCategoryField } from 'modules/supplier/outletOrServiceCategory/OutletOrServiceCategoryField';
import { StateFilterField, TypeFilterField } from '../Filter';

const resolveColumns = (visibility: Visibility): TableColumns<Row> => {
  switch (visibility) {
    case 'GLOBAL':
      return columns;
    case 'LOCAL':
      // no agency
      return columns.slice(1);
    case 'LOCAL_MANAGED':
    case 'LOCAL_TYPE':
      // no agency, no bonuses
      return columns.slice(1, 10);
  }
};

export function Client({ visibility, ...props }: VisibilityProps): ReactElement {
  const initialFilter = {
    date_from: formatISO(startOfYear(new Date()), { representation: 'date' }),
    date_to: formatISO(endOfYear(new Date()), { representation: 'date' }),
  };

  const url = `reporting/client/${visibility.toLowerCase()}${'type' in props ? `/${props.type}/` : '/'}`;

  const columns = useMemo(() => {
    return resolveColumns(visibility);
  }, [visibility]);

  return (
    <ReportTable<Row>
      url={url}
      columns={columns}
      defaultFilter={initialFilter}
      filterFields={
        <div className="flex-column">
          <div className="d-flex flex-row">
            <DateRangePeriodPicker />
            <ReportingGroupField
              name="client_groups"
              isMulti
              placeholder="Client group"
              className="ml-2"
              filter={{ direction: 'CLIENT' }}
              simple
            />
            <CompanyField name="clients" isMulti placeholder="Client" className="ml-2" simple includeBranches={true} />
            <CompanyField name="payers" isMulti placeholder="Payers" className="ml-2" simple includeBranches={true} />
            <CompanyField
              name="providers"
              isMulti
              placeholder="Provider"
              className="ml-2"
              simple
              includeBranches={true}
            />
            <OutletOrServiceCategoryField
              name="outlets_or_service_categories"
              isMulti
              placeholder="Outlets or service cat."
              className="ml-2"
              simple
            />
            {visibility === 'GLOBAL' && (
              <EnterpriseField
                name="agency"
                isMulti
                className="ml-1"
                defaultOptions
                placeholder="All agencies"
                simple
              />
            )}
          </div>

          <div className="d-flex flex-row">
            <StateFilterField />
            {visibility !== 'LOCAL_TYPE' && <TypeFilterField global={visibility === 'GLOBAL'} className="ml-1" />}
          </div>
        </div>
      }
    />
  );
}
